import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, StatusPill } from '@ui-kit';
import { useRevisionSnapshot } from '../hooks/useRevisionSnapshot';
import type { ApiError, RevisionType } from '../types/changeHistoryViewer';
import type { StatusTone } from '@ui-kit';

interface RevisionSnapshotDrawerProps {
  entityId: number;
  rev: number;
  onClose: () => void;
}

function revTypeTone(type: RevisionType | null | undefined): StatusTone {
  if (type === 'ADD') return 'success';
  if (type === 'DEL') return 'danger';
  return 'neutral';
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
}

/**
 * RevisionSnapshotDrawer — 단일 revision 시점 전체 상태 표시 (우측 drawer)
 */
export function RevisionSnapshotDrawer({ entityId, rev, onClose }: RevisionSnapshotDrawerProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const { data, isLoading, isError, error, refetch } = useRevisionSnapshot(entityId, rev);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const apiErr = error as unknown as ApiError | undefined;
  const errorMsg = (() => {
    if (!apiErr) return t('changeHistoryViewer.error.generic');
    if (apiErr.code === 'AUTH-403-001') return t('changeHistoryViewer.error.forbidden');
    if (apiErr.code === 'CHGHIST-404-001') return t('changeHistoryViewer.error.revisionNotFound');
    return apiErr.message ?? t('changeHistoryViewer.error.generic');
  })();

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.35)',
        }}
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="snapshot-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          width: 'min(520px, 90vw)',
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            flexShrink: 0,
          }}
        >
          <h2
            id="snapshot-drawer-title"
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('changeHistoryViewer.snapshot.title')} — rev {rev}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t('changeHistoryViewer.common.close')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              border: '1px solid var(--color-surface-border)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-lg)',
              color: 'var(--color-text-secondary)',
            }}
          >
            ×
          </button>
        </div>

        {/* 컨텐츠 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-4) var(--spacing-5)' }}>
          {isLoading && (
            <div
              aria-busy="true"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 28,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    opacity: 1 - i * 0.1,
                  }}
                />
              ))}
            </div>
          )}

          {isError && (
            <ErrorState
              title={t('changeHistoryViewer.snapshot.title')}
              message={errorMsg}
              onRetry={() => void refetch()}
            />
          )}

          {!isLoading && !isError && data && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              {/* 메타 */}
              <section aria-label={t('changeHistoryViewer.snapshot.title')}>
                <dl
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: 'var(--spacing-2) var(--spacing-4)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                    {t('changeHistoryViewer.snapshot.revision')}
                  </dt>
                  <dd style={{ margin: 0, fontFamily: 'var(--font-family-mono)', fontWeight: 600 }}>
                    {data.revision}
                  </dd>

                  <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                    {t('changeHistoryViewer.snapshot.revisionedAt')}
                  </dt>
                  <dd style={{ margin: 0 }}>
                    {new Date(data.revisionedAt).toLocaleString('ko-KR')}
                  </dd>

                  <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                    {t('changeHistoryViewer.snapshot.actor')}
                  </dt>
                  <dd style={{ margin: 0 }}>
                    {data.actor ?? t('changeHistoryViewer.common.unknown')}
                  </dd>

                  <dt style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                    {t('changeHistoryViewer.snapshot.type')}
                  </dt>
                  <dd style={{ margin: 0 }}>
                    {data.type ? (
                      <StatusPill tone={revTypeTone(data.type)}>
                        {t(`changeHistoryViewer.timeline.type.${data.type}`)}
                      </StatusPill>
                    ) : (
                      t('changeHistoryViewer.common.unknown')
                    )}
                  </dd>
                </dl>
              </section>

              {/* 스냅샷 필드 */}
              <section>
                <h3
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-2)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('changeHistoryViewer.snapshot.fields')}
                </h3>
                <div
                  style={{
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                  }}
                >
                  {Object.entries(data.snapshot).map(([key, val], idx) => (
                    <div
                      key={key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '140px 1fr',
                        borderBottom:
                          idx < Object.keys(data.snapshot).length - 1
                            ? '1px solid var(--color-surface-border)'
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 600,
                          color: 'var(--color-text-secondary)',
                          background: 'var(--color-surface-muted)',
                          borderRight: '1px solid var(--color-surface-border)',
                        }}
                      >
                        {key}
                      </div>
                      <div
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-primary)',
                          wordBreak: 'break-all',
                          background: 'var(--color-surface-background)',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {formatValue(val)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
